import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { api, SheetView } from '../../api';
import { imageToPlan, PdfPage, PdfPageThumb, renderPdfPage, renderPdfThumbs } from '../../walks/pdfPages';
import { fileProblem } from '../attach';
import {
  handleAt,
  hitShape,
  moveShape,
  recognize,
  resizeShape,
  shapeBox,
  shapeHandles,
  shapePoints,
  type Shape,
} from './shapes';
import { useIsMobile } from '../../lib/media';
import { useBackEntry } from '../../nav/useBackEntry';
import { PanelHead } from '../../nav/PanelHead';
import { IconBoard, IconCollapse, IconEraser, IconMinus, IconPlus, IconSave, IconUndo } from '../../components/icons';
import { useConfirm } from '../../components/Confirm';
import { ScreenMenu, ScreenMenuItem } from './ScreenMenu';
import type { CallCtx } from './callRoom';
import {
  BOARD_COLORS,
  BOARD_WIDTHS,
  MAX_SHEET_PDF_PAGES,
  Pt,
  Stroke,
  BoardView,
  BOARD_VIEW_FIT,
  BOARD_ZOOM_MAX,
  BOARD_ZOOM_MIN,
  blankSheetSize,
  boardFileKind,
  chunkStroke,
  decimatePoints,
  decodeBoardMsg,
  encodeBoardMsg,
  eraserHit,
  myLastStroke,
  moveStrokePts,
  removeStroke,
  panBy,
  shouldApplyIncoming,
  throttleSend,
  touchCenter,
  touchSpread,
  zoomAt,
} from './boardMath';

/*
 * Доска в звонке: панель рисования и лист, открытие и закрытие — только у
 * ведущего (раздел 3.3 спеки). Модель штриха, прореживание точек и попадание
 * ластика — boardMath.ts.
 *
 * Живой слой (раздел 4.2): законченный штрих (и его снятие отменой или
 * ластиком) уходит остальным по каналу данных комнаты мгновенно —
 * CallCtx.sendBoardData/onBoardData (callRoom.ts, CallProvider.tsx), тонкая
 * обвязка над LiveKit, доска про саму библиотеку не знает. Хранимый слой
 * (обычный запрос к серверу, как раньше) остаётся источником истины и уходит
 * параллельно, не дожидаясь ответа: он же и подстраховка на случай, если
 * пакет живого канала потерялся, — следующее полное чтение листа (по событию
 * «лист открыт/закрыт», приходящему через сервер) всё равно принесёт штрих.
 * Своя метка штриха живого слоя — nonce (Stroke.nonce): в отличие от id, её
 * отправитель знает сразу, не дожидаясь ответа сервера.
 */

/** Случайная метка штриха для живого слоя (раздел 4.2) — только для сверки на этой вкладке, не для сервера. */
const genNonce = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export type BoardApi = {
  sheet: SheetView | null;
  strokes: Stroke[];
  error: string;
  /** Ведущий звонка — только он открывает, загружает и закрывает лист. */
  isHost: boolean;
  color: string;
  setColor: (c: string) => void;
  widthIdx: number;
  setWidthIdx: (i: number) => void;
  tool: 'pen' | 'eraser';
  setTool: (t: 'pen' | 'eraser') => void;
  /** Штрих целиком по завершении жеста — точки уже в долях листа, без прореживания. */
  finishStroke: (pts: Pt[]) => void;
  /** Точка касания ластика — стирает свой штрих под ней, если попал (boardMath.eraserHit). */
  eraseAt: (p: Pt) => void;
  undo: () => void;
  canUndo: boolean;
  openBlank: () => void;
  /** Выбор файла (раздел 5 спеки): PDF с несколькими страницами открывает pagePicker,
   *  однострочный PDF и обычная картинка становятся подложкой сразу. */
  pickFile: (file: File) => void;
  /** Документ ждёт выбора страницы — только миниатюры (renderPdfThumbs), не полный
   *  размер: полноразмерный PNG делается только для выбранной страницы. */
  pagePicker: { file: File; sourceName: string; pages: PdfPageThumb[] } | null;
  choosePage: (page: PdfPageThumb) => void;
  cancelPagePicker: () => void;
  close: () => void;
  busy: boolean;
  /** Ход разбора PDF («страница N из M») — документ может быть на сотни страниц. */
  progress: string | null;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  /** Выделенная фигура — её и двигают; null, пока ничего не выделено. */
  picked: Stroke | null;
  /** Тап по листу: своя фигура под точкой становится выделенной. Вернёт, нашлась ли. */
  pickAt: (p: Pt) => boolean;
  /** Фигуру передвинули или растянули — новое место уходит всем и на сервер. */
  applyShape: (target: Stroke, shape: Shape) => void;
};

/**
 * Чем узнать штрих в списке, который пересоздаётся на каждый ответ сервера:
 * у своего свежего есть метка живого слоя, у прочитанного с сервера — номер.
 */
const strokeKey = (s: Stroke): string => s.nonce ?? `#${s.id}`;

/** Дальше этого от фигуры касание её не берёт (в долях листа) — размер пальца, не линии. */
const SHAPE_GRAB = 0.02;
/** Радиус попадания по ручке растяжения. */
const HANDLE_GRAB = 0.035;

export function useBoard(call: CallCtx): BoardApi & { closeIfSharing: () => Promise<void> } {
  const callId = call.callId;
  const isGuest = call.isGuest;
  const token = call.linkToken;
  const me = call.me;
  const sendBoardData = call.sendBoardData;
  const onBoardData = call.onBoardData;
  const mobile = useIsMobile();

  const [sheet, setSheet] = useState<SheetView | null>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [color, setColor] = useState<string>(BOARD_COLORS[0].value);
  const [widthIdx, setWidthIdx] = useState(1);
  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');
  const [collapsed, setCollapsed] = useState(mobile);
  const [pagePicker, setPagePicker] = useState<{ file: File; sourceName: string; pages: PdfPageThumb[] } | null>(
    null,
  );
  const [progress, setProgress] = useState<string | null>(null);
  /*
   * Выделенная фигура (решение Ильи 16.09.2026): хранится ключом, а не самим
   * штрихом, — список штрихов пересоздаётся на каждый ответ сервера, и
   * ссылка на объект протухла бы сразу.
   */
  const [pickedKey, setPickedKey] = useState<string | null>(null);

  const isHost = !isGuest && !!call.boardHostId && call.boardHostId === me;

  // Живой слой (раздел 4.2): nonce чужих штрихов, уже применённых с канала
  // данных, — защита от двойного применения (повторная доставка канала).
  // Живёт по сеансу листа: полное чтение (applySheet) сбрасывает список,
  // прежние живые метки к новому/другому листу отношения не имеют.
  const seenRef = useRef<Set<string>>(new Set());
  // Очередь и история отправок — предел частоты (раздел 4.3, boardMath.throttleSend).
  const sendQueueRef = useRef<Stroke[]>([]);
  const sendHistoryRef = useRef<number[]>([]);
  const sendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Отмена/ластик по штриху, у которого ещё нет номера сервера (id 0 — ответ
  // записи в полёте): вместо запроса с нулём, который сервер молча не найдёт,
  // ждём номер здесь и стираем штрих, когда он придёт (важно 4, 5).
  const pendingEraseRef = useRef<Map<string, (id: number) => void>>(new Map());
  // То же для переноса фигуры, которую взяли, не дождавшись номера: новое
  // место ждёт здесь и уходит на сервер, как только номер придёт.
  const pendingMoveRef = useRef<Map<string, { pts: Pt[]; shape: Shape }>>(new Map());

  const applySheet = (view: SheetView | null) => {
    setSheet(view);
    setStrokes(view?.strokes ?? []);
    seenRef.current = new Set();
    setPickedKey(null);
  };

  // Фоновое чтение — без баннера ошибки: неудача здесь не запрос человека,
  // а следующее чтение (по событию) само поправит дело.
  const refresh = useCallback(() => {
    if (!callId) {
      applySheet(null);
      return;
    }
    const req = isGuest ? (token ? api.roomSheetCurrent(token) : Promise.resolve(null)) : api.sheetCurrent(callId);
    req.then(applySheet).catch(() => {});
  }, [callId, isGuest, token]);

  // Вход в звонок и каждое «лист открыт»/«лист закрыт» — перечитать лист целиком
  // (раздел 4.2 договора): событие не несёт ни подложку, ни штрихи, только знак.
  // Пока звонок не «active» (входящий, исходящий), сам ещё не в разговоре — и
  // сервер («requireParticipant») читать лист не даст.
  useEffect(() => {
    if (call.phase !== 'active') return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, call.boardSignal, call.phase]);

  // Приём живого слоя (раздел 4.2): подписка живёт, пока звонок активен, и
  // не зависит от смены счётчика секунд и прочих частых полей CallCtx —
  // onBoardData стабильна (CallProvider.tsx, пустой список зависимостей).
  useEffect(() => {
    if (call.phase !== 'active') return;
    return onBoardData((bytes, from) => {
      const msg = decodeBoardMsg(bytes);
      if (!msg) return;
      // Автор — личность отправителя канала данных (from, проверена
      // медиасервером), а не поле author из тела сообщения (важно 7):
      // иначе участник мог бы прислать штрих или удаление от чужого имени
      // и стереть чужие штрихи со всех экранов.
      if (msg.t === 'stroke') {
        // Защита от двойного применения (раздел 4.2): свой же штрих,
        // вернувшийся по каналу, и повторная доставка чужого — не рисуем.
        if (!shouldApplyIncoming(msg.nonce, from, me, seenRef.current)) return;
        seenRef.current.add(msg.nonce);
        const stroke: Stroke = { id: 0, nonce: msg.nonce, author: from, color: msg.color, width: msg.width, pts: msg.pts };
        setStrokes((s) => [...s, stroke]);
      } else if (msg.t === 'move') {
        // Фигуру передвинул её автор — у нас меняются только точки: двигать
        // её здесь всё равно некому, фигура своя только у автора.
        if (from === me) return;
        setStrokes((s) => moveStrokePts(s, { nonce: msg.nonce, id: msg.id, author: from }, msg.pts));
      } else if (msg.t === 'remove') {
        if (from === me) return; // свой — уже снят локально
        setStrokes((s) => removeStroke(s, { nonce: msg.nonce, id: msg.id, author: from }));
      }
    });
  }, [call.phase, me, onBoardData]);

  // Новое место фигуры на сервере (решение Ильи 16.09.2026): тот же штрих,
  // другие точки. Не вышло — говорим и перечитываем лист: расходиться с тем,
  // что видят остальные, фигура не должна.
  const sendMove = useCallback(
    (strokeId: number, pts: Pt[], shape: Shape) => {
      const req = isGuest
        ? token
          ? api.roomSheetMove(token, strokeId, pts, shape)
          : Promise.resolve({ ok: true as const })
        : api.sheetMove(callId!, strokeId, pts, shape);
      req.catch(() => {
        setError('Не удалось передвинуть фигуру');
        refresh();
      });
    },
    [callId, isGuest, refresh, token],
  );

  // Отправка штриха: живой слой (канал данных, мгновенно) и хранимый слой
  // (обычный запрос, источник истины) — параллельно, не дожидаясь друг
  // друга (раздел 4.2). Ответ хранимого слоя подставляет настоящий id
  // вместо временного 0 — без этого ластик по свежему штриху до следующего
  // полного чтения листа ничего на сервере не находил (пункт 1).
  const dispatchStroke = useCallback(
    (stroke: Stroke) => {
      sendBoardData(
        encodeBoardMsg({ t: 'stroke', nonce: stroke.nonce!, author: stroke.author, color: stroke.color, width: stroke.width, pts: stroke.pts }),
      );
      const body = { color: stroke.color, width: stroke.width, pts: stroke.pts, ...(stroke.shape ? { shape: stroke.shape } : {}) };
      const req = isGuest
        ? token
          ? api.roomSheetStroke(token, body)
          : Promise.resolve(null)
        : api.sheetStroke(callId!, body);
      req
        .then((res) => {
          if (!res) return;
          // Сверка по метке живого слоя, а не по ссылке: фигуру могли
          // передвинуть, пока запись была в полёте, и объект штриха в списке
          // к этому времени уже другой — по ссылке номер бы не пришёлся.
          setStrokes((s) => s.map((x) => (x.nonce === stroke.nonce ? { ...x, id: res.id } : x)));
          // Штрих мог не успеть получить номер до отмены/ластика (важно 4) —
          // тогда запрос на удаление ждал здесь, а не уходил с id 0.
          const waiting = pendingEraseRef.current.get(stroke.nonce!);
          if (waiting) {
            pendingEraseRef.current.delete(stroke.nonce!);
            waiting(res.id);
          }
          // Фигуру могли передвинуть, пока запись была в полёте (важно 4):
          // тогда новое место ждало номера здесь.
          const moved = pendingMoveRef.current.get(stroke.nonce!);
          if (moved) {
            pendingMoveRef.current.delete(stroke.nonce!);
            sendMove(res.id, moved.pts, moved.shape);
          }
        })
        .catch(() => {
          // Штрих не записался вовсе — стирать и двигать по нему уже нечего.
          pendingEraseRef.current.delete(stroke.nonce!);
          pendingMoveRef.current.delete(stroke.nonce!);
          setStrokes((s) => s.filter((x) => x.nonce !== stroke.nonce));
          // Живой слой уже показал штрих остальным — без сигнала удаления он
          // остался бы только у них, хотя у автора и на сервере его нет (важно 6).
          sendBoardData(encodeBoardMsg({ t: 'remove', author: stroke.author, nonce: stroke.nonce }));
          setError('Штрих не сохранился — попробуйте ещё раз');
          setTimeout(() => setError(''), 2500);
        });
    },
    [callId, isGuest, sendBoardData, sendMove, token],
  );

  // Предел частоты отправки (раздел 4.3, boardMath.throttleSend): очередь на
  // случай, если предел точек в штрихе (chunkStroke) нарезал безостановочный
  // жест на куски быстрее, чем разрешено отправлять, — куски не теряются, а
  // ждут своей очереди.
  const drainQueue = useCallback(() => {
    sendTimerRef.current = null;
    const stroke = sendQueueRef.current[0];
    if (!stroke) return;
    const { delayMs, history } = throttleSend(sendHistoryRef.current, Date.now());
    if (delayMs > 0) {
      sendTimerRef.current = setTimeout(drainQueue, delayMs);
      return;
    }
    sendHistoryRef.current = history;
    sendQueueRef.current = sendQueueRef.current.slice(1);
    dispatchStroke(stroke);
    if (sendQueueRef.current.length) sendTimerRef.current = setTimeout(drainQueue, 0);
  }, [dispatchStroke]);

  const enqueueStroke = useCallback(
    (stroke: Stroke) => {
      sendQueueRef.current.push(stroke);
      if (sendTimerRef.current == null) drainQueue();
    },
    [drainQueue],
  );

  const finishStroke = useCallback(
    (pts: Pt[]) => {
      if (!pts.length) return;
      const width = BOARD_WIDTHS[widthIdx].value;
      /*
       * Распознавание фигур (решение Ильи 16.09.2026): примерный круг
       * становится ровным кругом, квадрат — квадратом, и так же треугольник и
       * прямая. Работает всегда, отдельного режима нет — выравнивание это то,
       * чего от доски ждут. Не понравилось — «Отменить» снимает фигуру, как
       * снял бы обычный штрих.
       *
       * Росчерк, в котором фигуры не видно, остаётся собой: подменять то, что
       * человек рисовал от руки, хуже, чем не подменить.
       */
      const shape = recognize(pts);
      const source = shape ? shapePoints(shape) : pts;
      // Прореживание (раздел 4.3, как в PhotoEditor.tsx), затем предел точек
      // в штрихе: безостановочный жест режется на несколько штрихов. Фигуру
      // прореживать нечего — у неё и так опорные точки.
      const parts = shape ? [source] : chunkStroke(decimatePoints(source));
      for (const chunkPts of parts) {
        const stroke: Stroke = { id: 0, nonce: genNonce(), author: me, color, width, pts: chunkPts, shape };
        setStrokes((s) => [...s, stroke]);
        enqueueStroke(stroke);
        // Узнанная фигура сразу выделяется: ручки видно, и поправить её можно
        // тем же движением, не отрывая руки (решение Ильи 16.09.2026).
        if (shape) setPickedKey(stroke.nonce!);
      }
    },
    [color, enqueueStroke, me, widthIdx],
  );

  /*
   * Фигуру берут тапом по ней (решение Ильи 16.09.2026: отдельного режима
   * нет). Своя — потому что и на сервере двигать чужие фигуры нельзя, а
   * ручки у чужой фигуры обещали бы то, чего не будет. Перебор с конца:
   * из наложенных друг на друга берётся верхняя.
   */
  const pickAt = useCallback(
    (p: Pt) => {
      for (let i = strokes.length - 1; i >= 0; i--) {
        const st = strokes[i];
        if (st.author === me && st.shape && hitShape(st.shape, p, SHAPE_GRAB)) {
          setPickedKey(strokeKey(st));
          return true;
        }
      }
      setPickedKey(null);
      return false;
    },
    [me, strokes],
  );

  const picked = useMemo(() => strokes.find((st) => st.shape && strokeKey(st) === pickedKey) ?? null, [pickedKey, strokes]);

  /*
   * Фигура переехала: на экране это новые точки, на сервере — тот же штрих с
   * новыми точками и новой фигурой (маршрут sheet/move). Живой слой уходит
   * сразу, хранимый — параллельно, как и у обычного штриха; у штриха без
   * номера новое место ждёт ответа записи (pendingMoveRef).
   */
  const applyShape = useCallback(
    (target: Stroke, shape: Shape) => {
      const pts = shapePoints(shape);
      const key = strokeKey(target);
      setStrokes((list) => list.map((st) => (strokeKey(st) === key ? { ...st, pts, shape } : st)));
      sendBoardData(encodeBoardMsg({ t: 'move', author: target.author, nonce: target.nonce, id: target.id || undefined, pts }));
      if (target.id) sendMove(target.id, pts, shape);
      else if (target.nonce) pendingMoveRef.current.set(target.nonce, { pts, shape });
    },
    [sendBoardData, sendMove],
  );

  // Удаление конкретного своего штриха на сервере — тот же маршрут, что и у
  // ластика, теперь используется и отменой (важно 5): прежний sheetUndo/
  // roomSheetUndo снимал «последний по нумерации сервера», а не тот штрих,
  // который снят на экране, — если предыдущий штрих ещё в полёте (не
  // записался), номера расходятся, и с сервера снимался не тот штрих.
  const sendErase = useCallback(
    (strokeId: number) => {
      const req = isGuest
        ? token
          ? api.roomSheetErase(token, strokeId)
          : Promise.resolve({ ok: true as const })
        : api.sheetErase(callId!, strokeId);
      req.catch(() => {
        setError('Не удалось стереть штрих');
        refresh();
      });
    },
    [callId, isGuest, refresh, token],
  );

  // Штрих без номера (id 0 — ответ записи ещё в полёте) стирать нулём нельзя:
  // сервер такой id не найдёт и молча ничего не удалит, штрих останется в
  // базе (важно 4). Дожидаемся настоящего номера — dispatchStroke вызовет
  // сохранённый здесь колбэк, как только он придёт.
  const eraseWhenReady = useCallback(
    (target: Stroke) => {
      if (target.id) {
        sendErase(target.id);
        return;
      }
      pendingEraseRef.current.set(target.nonce!, (id) => sendErase(id));
    },
    [sendErase],
  );

  const undo = useCallback(() => {
    const target = myLastStroke(strokes, me);
    if (!target) return;
    // Живой слой (раздел 4.2): остальные должны увидеть, что штрих исчез, не
    // дожидаясь следующего полного чтения листа.
    sendBoardData(encodeBoardMsg({ t: 'remove', author: target.author, nonce: target.nonce, id: target.id || undefined }));
    setStrokes((s) => s.filter((x) => x !== target));
    eraseWhenReady(target);
  }, [eraseWhenReady, me, sendBoardData, strokes]);

  // Ластик стирает свой штрих под точкой касания (boardMath.eraserHit) — не
  // обязательно последний; один раз за прикосновение, следующий разрешается,
  // когда список штрихов правда сменился.
  const erasingRef = useRef(false);
  const eraseAt = useCallback(
    (p: Pt) => {
      if (erasingRef.current) return;
      const target = eraserHit(p, strokes, me);
      if (!target) return;
      erasingRef.current = true;
      sendBoardData(encodeBoardMsg({ t: 'remove', author: target.author, nonce: target.nonce, id: target.id || undefined }));
      setStrokes((s) => s.filter((x) => x !== target));
      eraseWhenReady(target);
    },
    [eraseWhenReady, me, sendBoardData, strokes],
  );
  useEffect(() => {
    erasingRef.current = false;
  }, [strokes]);

  const openBlank = useCallback(() => {
    if (!callId || busy) return;
    setBusy(true);
    setError('');
    const { width, height } = blankSheetSize();
    api
      .sheetOpenBlank(callId, width, height)
      .then((view) => {
        applySheet(view);
        if (call.screenOn) call.stopScreen();
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось открыть лист'))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, call, callId]);

  // Открывает лист из уже готовой страницы (раздел 5 спеки): сама страница уже
  // отрисована в PNG браузером — либо единственная страница PDF/картинка (без
  // выбора, pickFile ниже), либо та, что выбрали в pagePicker. Свой busy-гейт
  // не ставим — оба места, откуда это зовут (pickFile и choosePage), уже сами
  // решают, когда можно: иначе тут же после setBusy(false) у pickFile
  // получился бы гоночный устаревший busy в замыкании.
  const openPageFile = useCallback(
    (page: PdfPage, sourceName: string) => {
      if (!callId) return;
      setBusy(true);
      setError('');
      api
        .sheetOpenPage(callId, page.png, page.width, page.height, sourceName, page.index + 1)
        .then((view) => {
          applySheet(view);
          if (call.screenOn) call.stopScreen();
          setPagePicker(null);
        })
        .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось открыть лист'))
        .finally(() => setBusy(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [call, callId],
  );

  // Выбор файла (раздел 5 спеки): PDF с одной страницей и обычная картинка
  // становятся подложкой сразу, как и раньше; PDF с несколькими страницами
  // просит выбрать одну (pagePicker) — их читает та же pdfjs-dist, что и в
  // обходах (FloorPlans.tsx). Не PDF и не картинка — понятный отказ сразу,
  // без попытки разбора.
  //
  // Разбор PDF — только миниатюрами (renderPdfThumbs): рендерить в полный
  // размер (2400 точек) все страницы альбома в 100–200 листов ради выбора
  // одной — минуты замирания посреди живого звонка и сотни мегабайт в
  // памяти. Полный размер делает renderPdfPage только для одной, выбранной
  // страницы — и для единственной страницы документа тоже, без лишнего
  // касания, выбирать не из чего.
  const pickFile = useCallback(
    (file: File) => {
      if (busy) return;
      setError('');
      const problem = fileProblem(file);
      if (problem) {
        setError(problem);
        return;
      }
      const kind = boardFileKind(file.type);
      if (kind === 'pdf') {
        setBusy(true);
        renderPdfThumbs(file, (done, total) => setProgress(`Разбираю страницу ${done} из ${total}`), MAX_SHEET_PDF_PAGES)
          .then((pages) => {
            if (pages.length === 1) {
              setProgress('Готовлю страницу…');
              return renderPdfPage(file, 0).then((page) => {
                setBusy(false);
                setProgress(null);
                openPageFile(page, file.name);
              });
            }
            setPagePicker({ file, sourceName: file.name, pages });
            setBusy(false);
            setProgress(null);
          })
          .catch((e) => {
            setError(e instanceof Error ? e.message : 'Не удалось разобрать PDF');
            setBusy(false);
            setProgress(null);
          });
      } else if (kind === 'image') {
        setBusy(true);
        imageToPlan(file)
          .then((page) => {
            setBusy(false);
            openPageFile(page, file.name);
          })
          .catch((e) => {
            setError(e instanceof Error ? e.message : 'Не удалось прочитать картинку');
            setBusy(false);
          });
      } else {
        setError('Нужен файл PDF или картинка PNG/JPEG');
      }
    },
    [busy, openPageFile],
  );

  const choosePage = useCallback(
    (page: PdfPageThumb) => {
      if (!pagePicker) return;
      setBusy(true);
      setError('');
      setProgress('Готовлю страницу…');
      renderPdfPage(pagePicker.file, page.index)
        .then((full) => {
          setProgress(null);
          openPageFile(full, pagePicker.sourceName);
        })
        .catch((e) => {
          setError(e instanceof Error ? e.message : 'Не удалось разобрать PDF');
          setBusy(false);
          setProgress(null);
        });
    },
    [pagePicker, openPageFile],
  );

  const cancelPagePicker = useCallback(() => setPagePicker(null), []);

  const close = useCallback(() => {
    if (!callId || busy) return;
    setBusy(true);
    setError('');
    api
      .sheetClose(callId)
      .then(() => applySheet(null))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось закрыть лист'))
      .finally(() => setBusy(false));
  }, [busy, callId]);

  // Показ экрана доску больше не закрывает сам (договор задачи): перед началом
  // показа клиент сам закрывает открытый лист тем же правом, что и кнопка
  // «Закрыть» — нет права или листа вовсе, тихо промолчать, показ начнётся всё равно.
  const closeIfSharing = useCallback(async () => {
    if (!callId || !isHost || !sheet) return;
    try {
      await api.sheetClose(callId);
      applySheet(null);
    } catch {
      /* нет прав к этому моменту или сеть — лист останется открытым */
    }
  }, [callId, isHost, sheet]);

  return {
    sheet,
    strokes,
    error,
    isHost,
    color,
    setColor,
    widthIdx,
    setWidthIdx,
    tool,
    setTool,
    finishStroke,
    eraseAt,
    undo,
    canUndo: !!myLastStroke(strokes, me),
    openBlank,
    pickFile,
    pagePicker,
    choosePage,
    cancelPagePicker,
    close,
    busy,
    progress,
    collapsed,
    setCollapsed,
    picked,
    pickAt,
    applyShape,
    closeIfSharing,
  };
}

/**
 * Рамка и ручки выделенной фигуры (решение Ильи 16.09.2026). Рисуются на той
 * же канве поверх штрихов: отдельный слой ради четырёх квадратиков заводить
 * незачем. `scale` — текущее приближение листа: рамка от него не толстеет и
 * ручки не разъезжаются, они про палец, а не про лист.
 */
function paintPick(ctx: CanvasRenderingContext2D, W: number, H: number, shape: Shape, scale: number) {
  const r = HANDLE_PX / scale;
  ctx.save();
  ctx.strokeStyle = PICK_COLOR;
  ctx.lineWidth = 1.5 / scale;
  ctx.setLineDash([5 / scale, 4 / scale]);
  const b = shapeBox(shape);
  ctx.strokeRect(b.minX * W, b.minY * H, (b.maxX - b.minX) * W, (b.maxY - b.minY) * H);
  ctx.setLineDash([]);
  ctx.fillStyle = '#fff';
  for (const h of shapeHandles(shape)) {
    ctx.beginPath();
    ctx.rect(h.x * W - r, h.y * H - r, r * 2, r * 2);
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Рамка выделения — синий палитры доски: канва не знает про токены CSS, а
 * заводить ради четырёх квадратиков новый цвет незачем.
 */
const PICK_COLOR = BOARD_COLORS.find((c) => c.cls === 'blue')!.value;
/** Полсторона ручки в точках канвы — палец, а не лист, поэтому число, а не доля. */
const HANDLE_PX = 7;

/** Жест без движения — тап: с него берут фигуру, а не рисуют. */
const TAP_DIST = 0.01;
const isTap = (pts: Pt[]): boolean =>
  pts.length > 0 && pts.every((p) => Math.hypot(p.x - pts[0].x, p.y - pts[0].y) <= TAP_DIST);

/** Заливает канву штрихами листа поверх подложки (сама подложка — отдельная картинка под канвой). */
function paintBoard(ctx: CanvasRenderingContext2D, W: number, H: number, strokes: Stroke[]) {
  ctx.clearRect(0, 0, W, H);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const s of strokes) {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = Math.max(1, s.width * W);
    ctx.beginPath();
    s.pts.forEach((p, i) => (i ? ctx.lineTo(p.x * W, p.y * H) : ctx.moveTo(p.x * W, p.y * H)));
    // одиночный тап — точка, а не пустота (как в PhotoEditor.tsx)
    if (s.pts.length === 1) ctx.lineTo(s.pts[0].x * W + 0.01, s.pts[0].y * H);
    ctx.stroke();
  }
}

/** Лист доски: подложка картинкой под штрихами, канва рисования поверх. Занимает `.call-screen` целиком. */
export function BoardStage({ board }: { board: BoardApi }) {
  const outer = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useRef<Pt[] | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  /* Масштаб и сдвиг листа (решение Ильи 11.09.2026): щипок двумя пальцами,
     колесо с Ctrl на компьютере и кнопки в углу сцены. Формат листа не
     меняется — меняется только то, как близко на него смотрят. */
  const [view, setView] = useState<BoardView>(BOARD_VIEW_FIT);
  /** Активные касания: по двум считается щипок, одно — рисует. */
  const touches = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinch = useRef<{ spread: number; center: { x: number; y: number } } | null>(null);
  /*
   * Ведение выделенной фигуры (решение Ильи 16.09.2026): `from` — фигура на
   * начало жеста, от неё и считается каждый кадр, чтобы округления не
   * копились; `now` — то, что видно сейчас.
   */
  const drag = useRef<{ mode: 'move' | 'resize'; handle: number; start: Pt; from: Shape; now: Shape; target: Stroke } | null>(null);
  const [ghost, setGhost] = useState<Shape | null>(null);
  const sheet = board.sheet;

  const render = useCallback(() => {
    const c = canvasRef.current;
    if (!c || !sheet) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const liveStroke: Stroke | null = live.current
      ? { id: 0, author: '', color: board.color, width: BOARD_WIDTHS[board.widthIdx].value, pts: live.current }
      : null;
    const picked = board.picked;
    // Пока фигуру ведут рукой, она рисуется на новом месте, а на сервер и
    // остальным уходит один раз — когда руку отпустили.
    const shown = ghost && picked ? board.strokes.map((st) => (st === picked ? { ...st, pts: shapePoints(ghost) } : st)) : board.strokes;
    paintBoard(ctx, c.width, c.height, liveStroke ? [...shown, liveStroke] : shown);
    const sel = ghost ?? picked?.shape;
    if (sel) paintPick(ctx, c.width, c.height, sel, view.scale);
  }, [board.color, board.picked, board.strokes, board.widthIdx, ghost, sheet, view.scale]);

  // Вписывание листа в свободное место сцены — тот же приём, что в PhotoEditor.tsx.
  useEffect(() => {
    const el = outer.current;
    if (!el || !sheet) return;
    const fit = () => {
      const s = Math.min(el.clientWidth / sheet.width, el.clientHeight / sheet.height);
      if (!Number.isFinite(s) || s <= 0) return;
      setBox({ w: Math.max(1, Math.floor(sheet.width * s)), h: Math.max(1, Math.floor(sheet.height * s)) });
    };
    fit();
    setView(BOARD_VIEW_FIT);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [sheet]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !box.w) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(box.w * dpr);
    c.height = Math.round(box.h * dpr);
    render();
  }, [box, render]);

  useEffect(render, [render]);

  if (!sheet) return null;

  const norm = (e: ReactPointerEvent<HTMLCanvasElement>): Pt => {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)),
    };
  };

  /** Точка касания в координатах сцены, от её середины: с ней считает zoomAt. */
  const stagePoint = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const r = outer.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return { x: e.clientX - (r.left + r.width / 2), y: e.clientY - (r.top + r.height / 2) };
  };

  const down = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* синтетические события */
    }
    touches.current.set(e.pointerId, stagePoint(e));
    // Второй палец превращает жест в щипок: начатый штрих при этом бросаем,
    // иначе на листе оставалась бы случайная черта от движения пальцев.
    if (touches.current.size === 2) {
      const [a, b] = [...touches.current.values()];
      pinch.current = { spread: touchSpread(a, b), center: touchCenter(a, b) };
      live.current = null;
      render();
      return;
    }
    const p = norm(e);
    if (board.tool === 'eraser') {
      board.eraseAt(p);
      return;
    }
    /*
     * Выделенную фигуру берут прямо за неё: за ручку — растянуть, за саму
     * фигуру — передвинуть. Мимо — обычное рисование, а тап мимо снимет
     * выделение (up ниже). Допуски делятся на приближение: на увеличенном
     * листе палец закрывает меньший кусок листа.
     */
    const sel = board.picked;
    if (sel?.shape) {
      const h = handleAt(sel.shape, p, HANDLE_GRAB / view.scale);
      if (h >= 0) {
        drag.current = { mode: 'resize', handle: h, start: p, from: sel.shape, now: sel.shape, target: sel };
        setGhost(sel.shape);
        return;
      }
      if (hitShape(sel.shape, p, SHAPE_GRAB / view.scale)) {
        drag.current = { mode: 'move', handle: -1, start: p, from: sel.shape, now: sel.shape, target: sel };
        setGhost(sel.shape);
        return;
      }
    }
    live.current = [p];
    render();
  };
  const move = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (touches.current.has(e.pointerId)) touches.current.set(e.pointerId, stagePoint(e));
    if (touches.current.size === 2 && pinch.current) {
      const [a, b] = [...touches.current.values()];
      const spread = touchSpread(a, b);
      const center = touchCenter(a, b);
      const prev = pinch.current;
      pinch.current = { spread, center };
      setView((v) => {
        const zoomed = prev.spread > 0 ? zoomAt(v, spread / prev.spread, center, box) : v;
        // щипок заодно двигает лист: пальцы редко стоят на месте
        return panBy(zoomed, center.x - prev.center.x, center.y - prev.center.y, box);
      });
      return;
    }
    const p = norm(e);
    if (board.tool === 'eraser') {
      board.eraseAt(p);
      return;
    }
    const d = drag.current;
    if (d) {
      d.now = d.mode === 'move' ? moveShape(d.from, p.x - d.start.x, p.y - d.start.y) : resizeShape(d.from, d.handle, p);
      setGhost(d.now);
      return;
    }
    if (!live.current) return;
    live.current.push(p);
    render();
  };
  const up = (e?: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e) touches.current.delete(e.pointerId);
    if (touches.current.size < 2) pinch.current = null;
    const d = drag.current;
    if (d) {
      drag.current = null;
      setGhost(null);
      // Взялись и отпустили, не сдвинув, — двигать нечего: лишний запрос и
      // лишняя рассылка остальным.
      if (d.now !== d.from) board.applyShape(d.target, d.now);
      else render();
      return;
    }
    if (!live.current) return;
    const pts = live.current;
    live.current = null;
    /*
     * Тап — это выбор фигуры, а не штрих: отдельного режима у фигур нет
     * (решение Ильи 16.09.2026), а точка от тапа на листе и так ничего не
     * значит. Тап мимо снимает выделение; тап мимо, когда выделять было
     * нечего, остаётся обычной точкой.
     */
    if (isTap(pts)) {
      const had = !!board.picked;
      if (board.pickAt(pts[0]) || had) {
        render();
        return;
      }
    }
    board.finishStroke(pts);
    render();
  };

  return (
    <div
      ref={outer}
      className="board-stage"
      onWheel={(e) => {
        // На компьютере приближают колесом с Ctrl — привычный жест браузера,
        // отдельной мыши-щипка не бывает.
        if (!e.ctrlKey) return;
        const r = outer.current?.getBoundingClientRect();
        if (!r) return;
        const center = { x: e.clientX - (r.left + r.width / 2), y: e.clientY - (r.top + r.height / 2) };
        setView((v) => zoomAt(v, e.deltaY < 0 ? 1.1 : 1 / 1.1, center, box));
      }}
    >
      {box.w > 0 && (
        <div
          className="board-sheet"
          style={{
            width: box.w,
            height: box.h,
            transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
          }}
        >
          {sheet.background && <img src={sheet.background} alt="" />}
          <canvas
            ref={canvasRef}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            onPointerLeave={up}
          />
        </div>
      )}
      {/* Кнопки приближения: щипок есть не на всяком устройстве, а вернуть
          лист целиком нужно уметь всегда (решение Ильи 11.09.2026). */}
      <div
        className="board-zoom"
        style={{
          // Держим кнопки в плоскости листа: лист стоит по центру сцены,
          // поэтому отступ считается от его края, а не от края сцены
          // (решение Ильи 11.09.2026). Сами кнопки не масштабируются.
          right: `calc(50% - ${box.w / 2}px + 10px)`,
          bottom: `calc(50% - ${box.h / 2}px + 10px)`,
        }}
      >
        <button
          type="button"
          onClick={() => setView((v) => zoomAt(v, 1 / 1.4, { x: 0, y: 0 }, box))}
          disabled={view.scale <= BOARD_ZOOM_MIN}
          title="Отдалить"
          aria-label="Отдалить"
        >
          <IconMinus />
        </button>
        <button
          type="button"
          onClick={() => setView(BOARD_VIEW_FIT)}
          disabled={view.scale === BOARD_ZOOM_MIN}
          title="Лист целиком"
          aria-label="Лист целиком"
        >
          <span className="mono">{Math.round(view.scale * 100)}%</span>
        </button>
        <button
          type="button"
          onClick={() => setView((v) => zoomAt(v, 1.4, { x: 0, y: 0 }, box))}
          disabled={view.scale >= BOARD_ZOOM_MAX}
          title="Приблизить"
          aria-label="Приблизить"
        >
          <IconPlus />
        </button>
      </div>
    </div>
  );
}

/** Панель рисования: цвета, толщины, ластик, отмена — для всех участников, лист уже открыт. */
export function BoardPanel({ board }: { board: BoardApi }) {
  if (!board.sheet) return null;
  return (
    <div className={`call-draw${board.collapsed ? ' off' : ''}`}>
      <div className="draw-panel">
        {BOARD_COLORS.map((c) => (
          <button
            key={c.cls}
            type="button"
            className={`draw-color ${c.cls}${board.tool === 'pen' && board.color === c.value ? ' on' : ''}`}
            onClick={() => {
              board.setColor(c.value);
              board.setTool('pen');
            }}
            aria-label={`Цвет ${c.cls}`}
          />
        ))}
        {BOARD_WIDTHS.map((w, i) => (
          <button
            key={w.cls}
            type="button"
            className={`draw-tool draw-width ${w.cls}${board.tool === 'pen' && board.widthIdx === i ? ' on' : ''}`}
            onClick={() => {
              board.setWidthIdx(i);
              board.setTool('pen');
            }}
            aria-label={['Тонкая', 'Средняя', 'Толстая'][i]}
          >
            <i />
          </button>
        ))}
        <button
          type="button"
          className={`draw-tool${board.tool === 'eraser' ? ' on' : ''}`}
          onClick={() => board.setTool(board.tool === 'eraser' ? 'pen' : 'eraser')}
          aria-label="Ластик"
          title="Ластик — стирает только ваши штрихи"
        >
          <IconEraser />
        </button>
        <button
          type="button"
          className="draw-tool"
          disabled={!board.canUndo}
          onClick={board.undo}
          aria-label="Отменить штрих"
          title="Отменить свой последний штрих"
        >
          <IconUndo />
        </button>
      </div>
      <button
        type="button"
        className="sb-collapse"
        title={board.collapsed ? 'Развернуть панель рисования' : 'Свернуть панель рисования'}
        onClick={() => board.setCollapsed(!board.collapsed)}
      >
        <IconCollapse />
      </button>
    </div>
  );
}

/**
 * «Сохранить в файл» — шапка сцены звонка (раздел 5.1 спеки), не панель
 * инструментов: панель про рисование, а сохранение про судьбу листа. Видна
 * только ведущему при открытом листе. Нажатие предупреждает через общий
 * диалог «да/нет» (useConfirm), что сохранение прервёт рисование и закроет
 * лист; после подтверждения — board.close(), та же сборка файла и закрытие,
 * что раньше делала кнопка доски в панели управления.
 */
export function BoardSaveButton({ board }: { board: BoardApi }) {
  const ask = useConfirm();
  if (!board.isHost || !board.sheet) return null;

  const onSave = async () => {
    const ok = await ask({
      title: 'Сохранить лист в файл?',
      text: 'Сохранение прервёт рисование и закроет лист.',
      okText: 'Сохранить',
    });
    if (ok) board.close();
  };

  return (
    <button
      type="button"
      className="call-b mini"
      disabled={board.busy}
      onClick={() => void onSave()}
      title="Сохранить в файл" data-tip="Сохранить в файл"
      aria-label="Сохранить в файл"
    >
      <IconSave />
    </button>
  );
}

/**
 * Кнопка доски в панели звонка: только у ведущего — открыть «чистый лист» /
 * «загрузить документ». Закрытие листа сюда больше не относится: по разделу
 * 5.1 спеки оно переехало в кнопку «Сохранить в файл» в шапке сцены
 * (BoardSaveButton ниже) — закрытие и сохранение это одно действие, и вести
 * его с двух разных кнопок незачем. Раздел 3.2 спеки запрещает открывать
 * новый лист, не закрыв текущий, поэтому кнопка при открытом листе не
 * пропадает, а становится неактивной: человеку нужно видеть, что доска
 * занята, а не гадать, куда делась кнопка — и меню «Чистый лист / Загрузить
 * документ» при живом листе не открывается вовсе, чтобы новый лист не затёр
 * текущий незаметно.
 */
/**
 * Кнопка доски в плашке звонка.
 *
 * `compact` — телефон (решение Ильи 11.09.2026): там доска живёт пунктами в
 * меню «ещё», а не своей кнопкой, поэтому сама кнопка не рисуется. Выбор
 * файла и выбор страницы остаются здесь: без них пункты меню некуда вести.
 */
export function BoardControl({
  board,
  compact,
  onItems,
}: {
  board: BoardApi;
  compact?: boolean;
  /** Отдать пункты наружу — их показывает меню «ещё» на телефоне. */
  onItems?: (items: ScreenMenuItem[]) => void;
}) {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const items: ScreenMenuItem[] = useMemo(
    () => [
      { label: 'Доска: чистый лист', onPick: board.openBlank },
      { label: 'Доска: загрузить документ', onPick: () => fileRef.current?.click() },
    ],
    [board.openBlank],
  );
  const busy = board.busy || !!board.pagePicker || !!board.sheet;
  useEffect(() => {
    if (!onItems) return;
    onItems(board.isHost && !busy ? items : []);
  }, [busy, board.isHost, items, onItems]);

  if (!board.isHost) return null;

  return (
    <>
      {!compact && (
      <button
        type="button"
        className={`call-b${board.sheet ? ' on' : ''}`}
        disabled={board.busy || !!board.pagePicker || !!board.sheet}
        onClick={(e) => setMenu(e.currentTarget)}
        title={board.sheet ? 'Лист уже открыт — сохраните его в шапке, чтобы открыть новый' : 'Доска'} data-tip={board.sheet ? 'Лист уже открыт — сохраните его в шапке, чтобы открыть новый' : 'Доска'}
        aria-label="Доска"
      >
        <IconBoard />
      </button>
      )}
      {menu && <ScreenMenu anchor={menu} items={items} onClose={() => setMenu(null)} />}
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) board.pickFile(file);
        }}
      />
      {board.pagePicker && <PagePicker board={board} pagePicker={board.pagePicker} />}
    </>
  );
}

/**
 * Выбор страницы загруженного PDF (раздел 5 спеки): миниатюры и номера —
 * список подходит и узкому экрану. Раскладка — то же окно/экран стопки, что
 * у выбора людей в звонок (InviteDialog.tsx): на десктопе backdrop+dialog, на
 * телефоне полноэкранная панель с системной «назад».
 */
function PagePicker({
  board,
  pagePicker,
}: {
  board: BoardApi;
  pagePicker: { file: File; sourceName: string; pages: PdfPageThumb[] };
}) {
  const mobile = useIsMobile();
  useBackEntry(mobile, board.cancelPagePicker);

  const body = (
    <div className="row-list">
      {pagePicker.pages.map((p) => (
        <button
          key={p.index}
          type="button"
          className="row"
          disabled={board.busy}
          onClick={() => board.choosePage(p)}
        >
          <span className="flp-thumb">
            <img src={p.thumb} alt="" />
          </span>
          <span className="row-text">
            <span className="row-title">Страница {p.index + 1}</span>
          </span>
        </button>
      ))}
    </div>
  );

  const foot = (
    <div className="dialog-foot">
      <button type="button" className="btn outline" onClick={board.cancelPagePicker}>
        Отмена
      </button>
    </div>
  );

  if (mobile) {
    return (
      <div className="call-pick">
        <PanelHead title="Выберите страницу" sub={pagePicker.sourceName} onBack={board.cancelPagePicker} />
        <div className="call-pick-body">{body}</div>
        {foot}
      </div>
    );
  }

  return (
    <div className="backdrop" onClick={(e) => e.target === e.currentTarget && board.cancelPagePicker()}>
      <div className="dialog" role="dialog" aria-modal="true">
        <PanelHead title="Выберите страницу" sub={pagePicker.sourceName} onBack={board.cancelPagePicker} />
        <div className="dialog-body">{body}</div>
        {foot}
      </div>
    </div>
  );
}
